import {Dayjs} from 'dayjs';
import {FC, useContext, useEffect, useMemo, useState} from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import {Autocomplete, Chip, FormControl, FormHelperText, Stack, TextField} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {ru} from 'date-fns/locale';
import {get, has, reduce} from 'lodash';
import {Controller, useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {useRecoilState} from 'recoil';
import {DocumentService} from '../../../api/services/document.service';
import {ServiceRequestService} from '../../../api/services/serviceRequest.service';
import {EventBusContext} from '../../../providers/EventBus';
import {createObjectURL, resolveAndDownloadBlob} from '../../../shared/helpers/resolveAndDownloadBlob';
import {IServiceRequestFilters} from '../../../shared/types/serviceRequest.types';
import {reportDataState} from '../../../store/reports/atoms';
import {IReportFilterValue, ReportFilter} from '../report-filter/ReportFilter';
import {ReportFilterItems} from '../report-filter/ReportFilterItems';
import {useGetDictsServiceTypesStatusesQuery} from '../../../global/services/service-types';
import {useDebounce} from '../../../hooks/useDebounce';
import {SRStatus} from '../../../global/api';

const mapStatusList = (statuses: SRStatus[]) =>
  statuses
    .filter((i) => !!i.statusCode && !!i.statusName)
    .map((i) => ({
      label: i.statusName || '',
      value: i.statusCode || '',
    }));

interface FormValues {
  endDate: null | string;
  startDate: null | string;
  statuses: ReturnType<typeof mapStatusList>;
  filters: IReportFilterValue[];
}

export const ReportExecuted: FC = () => {
  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      endDate: null,
      startDate: null,
      filters: [],
      statuses: [],
    },
  });
  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: {errors},
  } = form;
  const [report, setReport] = useRecoilState(reportDataState);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const {bus} = useContext(EventBusContext);
  const [searchStatus, setSearchStatus] = useState('');
  const debounceSearch = useDebounce(searchStatus, 300);

  const {data: statusesFromDicts} = useGetDictsServiceTypesStatusesQuery({});
  const statusList = mapStatusList(statusesFromDicts || []);

  const filteredStatusList = useMemo(() => {
    return debounceSearch
      ? statusList.filter((i) => !!i.label.toLowerCase().match(new RegExp(debounceSearch.toLowerCase())))
      : statusList;
  }, [debounceSearch, statusList]);

  const data = watch();

  const isButtonDisabled =
    !(data.startDate && data.endDate && !errors.startDate && !errors.endDate) || !data.statuses.length;

  const isLoadButtonDisabled = isButtonDisabled || previewLoading;
  const isPreviewButtonDisabled = isButtonDisabled || loading;

  const fetchReport = async () => {
    const data = getValues();

    if (!data.endDate || !data.startDate || !data.statuses.length)
      throw new Error('Отсутствует дата начала или дата окончания.');

    const filters = reduce(
      data.filters || [],
      (acc, filter) => {
        let value = get(filter, 'value.value', filter.value);

        if (has(value, 'name') && has(value, 'prefix')) {
          value = `${value.prefix} ${value.name}`;
        }

        acc[filter.filterBy as keyof IServiceRequestFilters] = value;
        return acc;
      },
      {} as IServiceRequestFilters,
    );

    const reportResponse = await ServiceRequestService.getDoneReport(
      data.startDate?.split('.')[0],
      data.endDate?.split('.')[0],
      data.statuses.map((i) => i.value),
      filters,
    );

    if (reportResponse.status === 204 || !reportResponse.data?.id || !reportResponse.data?.fileName) {
      bus?.emit('info', ['По заданным параметрам заявки не найдены']);
      return null;
    }

    const {id, fileName} = reportResponse.data;
    const resReport = await DocumentService.getContent(id);

    return {resReport, id, fileName};
  };

  const openNewTab = (blob: Blob, fileName: string) => {
    const url = createObjectURL(blob);
    const id = url.split('/').pop();
    window.open(`/reports/executed/preview/${id}?data=${encodeURIComponent(fileName)}`, '_blank');
  };

  const downloadReport = async () => {
    if (report) {
      resolveAndDownloadBlob(report.report, report.name);
      return;
    }

    try {
      setLoading(true);
      const reportData = await fetchReport();
      if (!reportData) return;
      const {resReport, fileName} = reportData;
      resolveAndDownloadBlob(resReport, fileName);
    } catch (error) {
      bus?.emit('error', [error]);
    } finally {
      setLoading(false);
    }
  };

  const previewReport = async () => {
    if (report) {
      openNewTab(report.report.data, report.name);
      return;
    }

    try {
      setPreviewLoading(true);
      const reportData = await fetchReport();
      if (!reportData) return;
      const {resReport, fileName} = reportData;
      setReport({name: fileName, report: resReport});
      openNewTab(resReport.data, fileName);
    } catch (error) {
      bus?.emit('error', [error]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleChangeStartDate = (newValue: Dayjs | null) => {
    if (!newValue) return;
    changeDateAndTime(newValue, true);
  };

  const handleChangeEndDate = (newValue: Dayjs | null) => {
    if (!newValue) return;
    changeDateAndTime(newValue, false);
  };

  const changeDateAndTime = (newValue: Dayjs | null, isStart: boolean) => {
    if (newValue) {
      const dateKey = isStart ? 'startDate' : 'endDate';

      setValue(dateKey, newValue.toISOString(), {
        shouldValidate: true,
        shouldDirty: true,
      });
      setReport(null);
    }
  };

  const handleFilterSubmit = (filters: IReportFilterValue) => {
    setValue('filters', [...data.filters, filters]);
    setReport(null);
  };

  const handleFilterRemove = (filters: IReportFilterValue[]) => {
    setValue('filters', filters);
    setReport(null);
  };

  useEffect(() => {
    if (data.startDate && data.endDate) {
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);

      if (end.getTime() < start.getTime() || end.getTime() === start.getTime()) {
        toast.error('Выбран некорректный период', {
          containerId: 'default',
          toastId: 'executedReport',
        });
        setError('endDate', {message: 'Выберите другую дату'});
        setError('startDate', {message: 'Выберите другую дату'});
      } else if ('endDate' in errors || 'startDate' in errors) {
        clearErrors();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.startDate, data.endDate]);

  return (
    <Stack direction="column" sx={{px: 4, py: 4}} spacing={3}>
      <FormControl>
        <Controller
          name="statuses"
          control={control}
          render={({field: {value, onChange}}) => (
            <Autocomplete
              disablePortal
              id="statuses-select"
              sx={{
                maxWidth: 600,
              }}
              options={filteredStatusList}
              value={value}
              multiple
              size="small"
              onChange={(_, val) => {
                onChange(val);
                setReport(null);
              }}
              disableClearable
              renderTags={(values) =>
                values.map((val) => (
                  <Chip
                    key={val.value}
                    label={val.label}
                    size="small"
                    onDelete={() => {
                      setValue(
                        'statuses',
                        getValues('statuses').filter((i) => i.value !== val.value),
                        {shouldDirty: true},
                      );
                      setReport(null);
                    }}
                  />
                ))
              }
              inputValue={searchStatus}
              onInputChange={(_, value) => {
                setSearchStatus(value);
              }}
              filterOptions={(options) =>
                options.filter((option) => !getValues('statuses').find((o) => o.value === option.value))
              }
              renderInput={(params) => <TextField sx={{backgroundColor: 'white'}} {...params} label="Статус заявки" />}
              noOptionsText="Ничего не найдено"
            />
          )}
        />
      </FormControl>
      <LocalizationProvider
        localeText={{
          clearButtonLabel: 'Сбросить',
          okButtonLabel: 'Подтвердить',
          dateTimePickerToolbarTitle: 'Выбрать дату и время',
          datePickerToolbarTitle: 'Выбрать дату',
        }}
        dateAdapter={AdapterDateFns}
        adapterLocale={ru}
      >
        <Stack direction="row" spacing={1} maxWidth={600} justifyContent="space-between">
          <FormControl fullWidth>
            <Controller
              name="startDate"
              control={control}
              render={({fieldState: {error}}) => (
                <>
                  <DateTimePicker
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!error?.message,
                        sx: {backgroundColor: 'white'},
                      },
                      actionBar: {actions: ['clear', 'accept']},
                    }}
                    format={'dd.MM.yyyy HH:mm'}
                    label="Дата/время начала периода"
                    timezone="Europe/Moscow"
                    disableFuture
                    minutesStep={1}
                    onChange={handleChangeStartDate}
                  />
                  <FormHelperText error>{error?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <Controller
              name="endDate"
              control={control}
              render={({fieldState: {error}}) => (
                <>
                  <DateTimePicker
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!error?.message,
                        sx: {backgroundColor: 'white'},
                      },
                      actionBar: {actions: ['clear', 'accept']},
                    }}
                    format={'dd.MM.yyyy HH:mm'}
                    label="Дата/время конца периода"
                    timezone="Europe/Moscow"
                    disableFuture
                    minutesStep={1}
                    onChange={handleChangeEndDate}
                  />
                  <FormHelperText error>{error?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        </Stack>
      </LocalizationProvider>

      <Stack maxWidth={600} minHeight={100} spacing={4} justifyContent="space-between">
        <ReportFilter onChange={handleFilterSubmit} selectedFilters={data.filters} />
        <ReportFilterItems items={data.filters} onRemove={handleFilterRemove} />
      </Stack>

      <Stack direction="row" spacing={2} maxWidth={600} justifyContent="flex-end">
        <LoadingButton
          startIcon={<DownloadIcon />}
          variant="outlined"
          loading={loading}
          onClick={downloadReport}
          disabled={isLoadButtonDisabled}
          sx={{
            backgroundColor: 'white',
          }}
        >
          Скачать отчет
        </LoadingButton>
        <LoadingButton
          variant="outlined"
          loading={previewLoading}
          onClick={previewReport}
          disabled={isPreviewButtonDisabled}
          sx={{
            backgroundColor: 'white',
          }}
        >
          Просмотреть отчет
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
